import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { SchemaElement, SchemaRegion } from "../../api/restaurantSchema";
import TableView from "./TableView";
import ObjectView from "./ObjectView";
import { FLOOR_GRID_CLASS, floorGridStyle } from "./floorGrid";
import ZoomControls from "./ZoomControls";
import { useCanvasPan } from "./useCanvasPan";
import {
  elementPixelBox,
  elementsInPaintOrder,
  type ElementBox,
  type LogicalPoint,
} from "./stageLayout";
import type { TableStatus } from "./tableTones";
import type { SchemaStageState } from "./useSchemaStage";

/** Какво получава `renderOverlay`: мащабът и къде е всеки елемент в пиксели. */
export interface StageLayout {
  scale: number;
  // Размерът на видимото платно в пиксели.
  width: number;
  height: number;
  /** Правоъгълникът на елемента в пиксели спрямо горния ляв ъгъл на платното. */
  boxOf: (element: SchemaElement) => ElementBox;
}

type Props = {
  stage: SchemaStageState;
  region: SchemaRegion;
  // Избор в редактора (контур + ъглови маркери, отгоре на останалите).
  selectedId?: number | null;
  /** Логическата позиция на елемента (редакторът я сменя, докато се влачи). */
  positionOf?: (element: SchemaElement) => LogicalPoint;
  /** Допълнителни props/класове за обвивката на елемента (влаченето в редактора). */
  getElementProps?: (element: SchemaElement) => React.HTMLAttributes<HTMLDivElement>;
  /** Статусът на маса (тон, надпис, чип, точка, ореол). */
  tableStatus?: (element: SchemaElement) => TableStatus | undefined;
  /** Hover title на елемента; по подразбиране „Маса 1 · 4 места" за масите. */
  elementTitle?: (element: SchemaElement) => string | undefined;
  /** Клик/тап (или Enter) върху маса, която не е `disabled`. */
  onTableClick?: (element: SchemaElement) => void;
  /** Двоен клик върху маса, която не е `disabled` („Маркиране": отваря масата). */
  onTableDoubleClick?: (element: SchemaElement) => void;
  /**
   * Докосване (без местене) на платното — редакторът маха избора, поповърът се
   * затваря. Вика се и при докосване на маса (преди клика ѝ); дали е било по
   * празното място — `isFloorTap(event)`.
   */
  onCanvasTap?: (event: React.PointerEvent<HTMLDivElement>) => void;
  /** Показва се в празен регион. */
  emptyState?: ReactNode;
  /** „auto" — зуумът плава долу вдясно на широко платно, иначе горе вдясно; „none" — рисува го потребителят. */
  zoomControls?: "auto" | "none";
  /** С рамка и заоблени ъгли (самостоятелно платно) или без (вътре в карта). */
  framed?: boolean;
  /** Слой ВЪРХУ елементите, в пиксели на платното (мести се с тях): поповъри, ленти, помощни линии. */
  renderOverlay?: (layout: StageLayout) => ReactNode;
  /** Слой ПОД масите (над пода): връзките между масите на една резервация. */
  renderUnderlay?: (layout: StageLayout) => ReactNode;
  /** Плаващи неща в платното, които не се местят с него (лентата за добавяне, банер…). */
  children?: ReactNode;
};

/**
 * Сцената на схемата: подът (милиметрова хартия по цялото платно) и
 * елементите на региона върху безкрайно платно — изгледът (мащаб и
 * отместване) е на `useSchemaStage`, местенето с влачене по пода — на
 * `useCanvasPan`. Регионът няма видима граница и не ограничава елементите.
 * Без редакция — редакторът (`SchemaCanvas`) добавя влачене, избор и лентите
 * през props/`children`, модулите със статуси (резервации) — `tableStatus` +
 * `onTableClick` + `renderOverlay`.
 */
const SchemaStage = ({
  stage,
  region,
  selectedId = null,
  positionOf = (element) => ({ x: element.x, y: element.y }),
  getElementProps,
  tableStatus,
  elementTitle,
  onTableClick,
  onTableDoubleClick,
  onCanvasTap,
  emptyState,
  zoomControls = "auto",
  framed = true,
  renderOverlay,
  renderUnderlay,
  children,
}: Props) => {
  const { t } = useTranslation();
  const { scale, origin } = stage;
  const pan = useCanvasPan(stage, onCanvasTap);

  const layout: StageLayout = {
    scale,
    width: stage.viewportSize.width,
    height: stage.viewportSize.height,
    boxOf: (element) => elementPixelBox(element, positionOf(element), scale, origin),
  };

  const defaultTitle = (element: SchemaElement) =>
    element.kind === "table"
      ? t("restaurantSchema.canvas.tableTitle", {
          name: element.name,
          seats: t("restaurantSchema.counts.seats", { count: element.seats ?? 0 }),
        })
      : undefined;

  return (
    <div
      className={`relative min-h-0 flex-1 overflow-hidden bg-surface ${
        framed ? "rounded-[14px] border border-shade-2" : ""
      }`}
    >
      {/* Платното: подът с карето (тръгва от логическата точка 0, 0) и слой
          с елементите в пиксели на платното. Не се скролва — мести се изгледът. */}
      <div
        ref={stage.viewportRef}
        className={`absolute inset-0 overflow-hidden ${FLOOR_GRID_CLASS} ${
          stage.canPan ? "touch-none cursor-grab active:cursor-grabbing" : ""
        }`}
        style={floorGridStyle(scale, origin)}
        {...pan.handlers}
      >
        {region.elements.length === 0 && emptyState}

        {renderUnderlay?.(layout)}

        {elementsInPaintOrder(region.elements).map((element) => {
          const { x, y } = positionOf(element);
          const isSelected = element.id === selectedId;
          const isTable = element.kind === "table";
          const status = isTable ? tableStatus?.(element) : undefined;
          const isClickable = isTable && Boolean(onTableClick) && !status?.disabled;
          const extra = getElementProps?.(element) ?? {};
          const cursor = !onTableClick || !isTable
            ? ""
            : isClickable
              ? "cursor-pointer"
              : "cursor-not-allowed";
          return (
            <div
              key={element.id}
              data-schema-element=""
              title={(elementTitle ?? defaultTitle)(element)}
              {...(isClickable
                ? {
                    role: "button",
                    tabIndex: 0,
                    onClick: () => onTableClick?.(element),
                    onDoubleClick: onTableDoubleClick
                      ? () => onTableDoubleClick(element)
                      : undefined,
                    onKeyDown: (event: React.KeyboardEvent) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      onTableClick?.(element);
                    },
                  }
                : {})}
              {...extra}
              className={`absolute select-none ${cursor} ${
                isSelected || status?.highlighted ? "z-10" : ""
              } ${extra.className ?? ""}`}
              style={{
                left: origin.left + x * scale,
                top: origin.top + y * scale,
                transform: `translate(-50%, -50%) rotate(${element.rotation}deg)`,
              }}
            >
              {isTable ? (
                <TableView
                  name={element.name ?? ""}
                  shape={element.tableShape ?? "round"}
                  seats={element.seats ?? 4}
                  size={element.size}
                  rotation={element.rotation}
                  scale={scale}
                  selected={isSelected}
                  status={status}
                />
              ) : (
                <ObjectView
                  element={element}
                  scale={scale}
                  selected={isSelected}
                  grabbable={Boolean(getElementProps)}
                />
              )}
            </div>
          );
        })}

        {renderOverlay?.(layout)}
      </div>

      {children}
      {zoomControls === "auto" && (
        <ZoomControls placement={stage.isWide ? "bottom" : "top"} {...stage.zoomControls} />
      )}
    </div>
  );
};

/** Докосването на платното е по празното място, не по маса или обект. */
// eslint-disable-next-line react-refresh/only-export-components
export const isFloorTap = (event: React.PointerEvent<HTMLDivElement>) =>
  !(event.target instanceof Element && event.target.closest("[data-schema-element]"));

export default SchemaStage;
